/**
 * The template's own file, with the one thing in it that is data replaced: the artwork.
 *
 * `packages/isomorphic-core/src/components/logo.tsx` draws the vendor's "isomorphic" wordmark as
 * vector paths. Shipping a purchased template's own logo in a customer's back office is not a
 * styling decision, it is the wrong brand. The signature is unchanged - `iconOnly` plus SVG props,
 * which is what the copied `sidebar.tsx`, `header.tsx` and `auth-wrapper-one.tsx` call it with -
 * so none of them needed an edit beyond dropping the vendor's two <img> tags.
 *
 * ## The mark
 *
 * Infra Junction's mark (the brand suite's `ij-mark-blue.svg`, 2026-10-08): a hexagonal frame,
 * open at the bottom, around an i and a J, in the brand's `#1a3e8c`. The frame is a stroke and the
 * letters are fills, so it stays sharp from the 16px favicon up. The same paths are in
 * `steelyard-admin/public/{favicon,logo,logo-primary,logo-short}.svg` and the portal's
 * `components/steelyard-mark.tsx` / `app/icon.svg`, for the copied template files that take a URL
 * rather than a component.
 *
 * ## The icon: the mark on a white tile
 *
 * `#1a3e8c` on the dark scheme's near-black surface is barely visible, and a `currentColor` mark
 * would lose the brand colour in light mode. A white tile (the suite's light app icon, mark at
 * 88/144 of the tile) keeps the brand's blue-on-white in both schemes (on the light top bar the
 * tile simply disappears into the page).
 *
 * ## The full logo: the suite's horizontal lockup
 *
 * The mark at full height with no tile, then INFRA over JUNCTION in Barlow Semi Condensed 700
 * (`index.html` loads it) at 45% of the mark's height, line height 0.92, 6.7 units apart — the
 * suite's own proportions. The mark and the wordmark are the brand's blue in light mode and both
 * white in dark (the suite's "reversed on dark"). The box is fitted to the content and the logo
 * draws 40px tall at its natural width: the copied callers size it for the vendor's wide
 * wordmark (`w-full` in a 168px link, `max-w-[155px]`), which would otherwise stretch this one
 * to 55px tall. Each line's `textLength` is its measured width in the real face, so a fallback
 * face is squeezed to fit rather than clipped while the web font loads.
 */

/** The mark in the suite's own -4 -6 318 362 box: the frame (a stroke), then the i and J (fills). */
const FRAME = 'M291,263 L291,93 L154,18 L18,93 L18,268 L106,319';
const GLYPHS =
  'M78,139 L127,122 L127,171 L78,177 Z M85,187 L127,187 L127,352 L85,328 Z M157,133 L222,120 L222,298 C222,330 202,350 172,350 L157,350 L157,312 L166,312 C177,312 184,305 184,294 L184,151 L157,155 Z';

const BRAND_BLUE = '#1a3e8c';

function Mark() {
  return (
    <>
      <path d={FRAME} fill="none" stroke="currentColor" strokeWidth="36" />
      <path d={GLYPHS} fill="currentColor" />
    </>
  );
}

export default function Logo({ iconOnly = false, style, ...props }: { iconOnly?: boolean } & React.SVGProps<SVGSVGElement>) {
  if (iconOnly) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" role="img" aria-label="Infra Junction" style={style} {...props}>
        <rect width="32" height="32" rx="7" fill="#ffffff" />
        <svg x="7.63" y="6.55" width="17.17" height="19.55" viewBox="-4 -6 318 362" color={BRAND_BLUE}>
          <Mark />
        </svg>
      </svg>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 97 32"
      height="40"
      preserveAspectRatio="xMinYMid meet"
      fill="none"
      role="img"
      aria-label="Infra Junction"
      style={{ ...style, width: 'auto' }}
      {...props}
    >
      <g className="text-[#1a3e8c] dark:text-white">
        <svg width="28.1" height="32" viewBox="-4 -6 318 362">
          <Mark />
        </svg>
        {/* A presentation attribute cannot read a custom property, so the face and its fallback
            (`var(--font-lexend)`, the theme's heading stack) go through `style`. */}
        <g
          fill="currentColor"
          fontSize="14.5"
          fontWeight="700"
          letterSpacing="0.29"
          style={{ fontFamily: "'Barlow Semi Condensed', var(--font-lexend)" }}
        >
          <text x="34.8" y="14.4" textLength="37" lengthAdjust="spacingAndGlyphs">
            INFRA
          </text>
          <text x="34.8" y="27.74" textLength="61.8" lengthAdjust="spacingAndGlyphs">
            JUNCTION
          </text>
        </g>
      </g>
    </svg>
  );
}
